// Copyright (c) Docugami, Inc. All rights reserved.

import dayjs from "dayjs";
import { OrganizationAccount, SubscriptionType, SubscriptionStatus } from "networking/OrganizationApi";
import { AppState } from "store";

export function validateUserEmail(userEmail: string): boolean {
    return (/^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,63})+$/.test(userEmail));
}

export function getDaysLeft(expirationDate: Date, currentDate: Date): number {
    const expiryDate = dayjs(expirationDate);
    const daysBetween = Math.round(expiryDate.diff(currentDate, 'days', true));
    return Math.max(daysBetween, 0);
}

// Using 1 less than max number of users as threshold for prompting subscription upgrade
export function maxUsersThreshold(numUsers: number, maxUsers: number) {
    return maxUsers <= numUsers + 1;
}

// Using 70% of max pages allowed as page count threshold for prompting subscription upgrade
export function maxPagesThreshold(numPages: number, maxPages: number) {
    return numPages / maxPages >= 0.7;
}

// Using 5 days left as threshold for prompting subscription upgrade
export function daysLeftThreshold(expirationDate: Date) {
    return getDaysLeft(expirationDate, new Date()) < 5;
}

export function maxPageLimitReached(organizationAccount: OrganizationAccount | undefined): boolean {
    if (organizationAccount?.reachedPageLimit === undefined) {
        return false;
    }

    return !!organizationAccount.reachedPageLimit;
}

export function selectIsTrialSubscription(state: AppState) {
    return state.organizations.organizationAccount?.subscription.subscriptionType === SubscriptionType.Trial;
}

export function selectIsActivePaidSubscription(state: AppState) {
    return state.organizations.organizationAccount?.subscription.subscriptionId &&
        state.organizations.organizationAccount?.subscription.status === SubscriptionStatus.Active;
}

export function selectHasSubscriptionHistory(state: AppState) {
    return !!state.organizations.organizationAccount?.subscription.subscriptionId;
}

export function selectIsLimitedOrganization(state: AppState) {
    return !!state.organizations.organizationAccount?.expired;
}

export function selectSubscriptionFriendlyName(state: AppState) {
    return getFriendlyNameForSubscriptionType(state.organizations.organizationAccount?.subscription.subscriptionType);
}

export function getFriendlyNameForSubscriptionType(type?: SubscriptionType) {
    switch (type) {
        case SubscriptionType.Trial:
            return "Free Trial";
        case SubscriptionType.Basic:
            return "Professional";
        case SubscriptionType.Team:
            return "Team";
        case SubscriptionType.Business:
            return "Business";
        case SubscriptionType.Enterprise:
            return "Enterprise";
        default:
            return "";
    }
}
